import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import type { AppearancePreviewTab, OnboardingStepId } from "../types";
import { AgentPreviewAvatar, AgentPreviewBack } from "@/assets/AiBotAssets";
import { State } from "@/redux/Types";
import environment from "././../../../constants/Environment";

type OnBoardingPreviewProps = {
  activeStep: OnboardingStepId;
  appearancePreviewTab: AppearancePreviewTab;
  setAppearancePreviewTab: (tab: AppearancePreviewTab) => void;
  botData?: any;
  canLoadAgentPreview: boolean;
  className?: string;
};

const appearanceTabs: Array<{
  id: AppearancePreviewTab;
  label: string;
}> = [
  { id: "welcome", label: "Welcome Screen" },
  { id: "conversation", label: "Chat Preview" },
];

const INITIAL_IFRAME_TAB: AppearancePreviewTab = "welcome";

const STATIC_PREVIEW_MESSAGES = [
  { text: "Hey!", role: "user" },
  {
    text: "Hey there! Great to meet you! I'd love to help with whatever you need today",
    role: "assistant",
  },
];

const chatPreviewMessages = [
  { id: "intro", messageClassName: "agent-preview-message--short" },
  { id: "reply", messageClassName: "agent-preview-message--long" },
];

const AgentPreviewPlaceholder = ({ loading }: { loading: boolean }) => {
  return (
    <div className={`agent_preview ${loading ? "agent_preview-loading" : ""}`}>
      <div className="agent-preview-header">
        <AgentPreviewBack />
        <span className="agent-preview-avatar">
          <AgentPreviewAvatar className="h-[24px] w-[24px]" />
        </span>
        <div className="agent-preview-title">
          <span className="agent-preview-skeleton agent-preview-title-line agent-preview-title-line--short" />
          <span className="agent-preview-skeleton agent-preview-title-line agent-preview-title-line--long" />
        </div>
      </div>

      <div className="agent-preview-body">
        {chatPreviewMessages.map((message) => (
          <div className="agent-preview-row" key={message.id}>
            <span className="agent-preview-avatar h-[32px] w-[32px]">
              <AgentPreviewAvatar className="h-[18px] w-[18px]" />
            </span>
            <div className="agent-preview-bubble-group">
              <span className="agent-preview-skeleton agent-preview-meta-line" />
              <span
                className={`agent-preview-skeleton agent-preview-message ${message.messageClassName}`}
              />
            </div>
          </div>
        ))}

        <span className="agent-preview-skeleton agent-preview-card" />
      </div>

      <div className="agent-preview-input">
        <span className="agent-preview-skeleton agent-preview-input-line" />
        <span className="agent-preview-send">
          <svg
            className=""
            xmlns="http://www.w3.org/2000/svg"
            width="15"
            height="15"
            viewBox="0 0 16 17"
            fill="none"
          >
            <path
              d="M1.12457 0.888736L15.5045 8.07234C15.7365 8.18823 15.8318 8.47283 15.7175 8.70801C15.6719 8.80185 15.597 8.87778 15.5045 8.92402L1.12487 16.1075C0.892888 16.2234 0.612165 16.1267 0.497855 15.8915C0.447871 15.7886 0.436284 15.6709 0.465235 15.5601L1.88993 10.1057C1.93705 9.92536 2.08403 9.78969 2.26558 9.759L8.70978 8.66968C8.78873 8.65634 8.85401 8.6035 8.88538 8.53167L8.90224 8.47457C8.92046 8.36373 8.85976 8.25804 8.76172 8.21654L8.70978 8.20141L2.22919 7.10639C2.04757 7.0757 1.90054 6.93995 1.85347 6.75949L0.46491 1.436C0.398763 1.18253 0.547815 0.922697 0.797827 0.855636C0.907113 0.826322 1.02317 0.838079 1.12457 0.888736Z"
              fill="#fff"
            />
          </svg>
        </span>
      </div>
    </div>
  );
};

const OnBoardingPreview = ({
  activeStep,
  appearancePreviewTab,
  setAppearancePreviewTab,
  botData,
  canLoadAgentPreview,
  className = "",
}: OnBoardingPreviewProps) => {
  const [iframeUrl, setIframeUrl] = useState<string | undefined>(undefined);
  const [welcomeIframeLoaded, setWelcomeIframeLoaded] = useState(false);
  const [conversationIframeLoaded, setConversationIframeLoaded] =
    useState(false);
  const [staticMessagesReady, setStaticMessagesReady] = useState(false);
  const welcomeIframeRef = useRef<HTMLIFrameElement | null>(null);
  const conversationIframeRef = useRef<HTMLIFrameElement | null>(null);
  const hasSentInitialMessageRef = useRef(false);
  const iframeUrlRef = useRef<string | undefined>(undefined);
  const subDomain = useSelector(
    (state: State) => state.user.companyDetails.subDomain,
  );
  const environmentData = environment();
  const botPreviewUrl = botData?.url ?? botData?.Url;
  const liveUrl =
    environmentData.ENVIRONMENT === "production"
      ? environmentData.LIVEBOT_URL.replace("<sub-domain>", subDomain)
      : environmentData.LIVEBOT_URL;
  const shouldLoadLivePreview = activeStep >= 2 && canLoadAgentPreview;

  useEffect(() => {
    if (shouldLoadLivePreview) {
      // If iframe was already created, don't recreate it (preserves conversation)
      if (iframeUrlRef.current) return;

      setWelcomeIframeLoaded(false);
      setConversationIframeLoaded(false);
      const appId = botPreviewUrl ? `preview/${botPreviewUrl}` : undefined;

      if (!appId) {
        setIframeUrl(undefined);
        return;
      }

      const iframeScreenSyncScript = `
              window.pendingChatbotTab = window.location.hash === '#conversation'
                ? 'conversation'
                : '${INITIAL_IFRAME_TAB}';

              function getMainIframe() {
                var mainIframe = document.querySelector('.main-iframe');
                if (mainIframe) {
                  mainIframe.setAttribute('allow', 'microphone *; clipboard-write *');
                }
                return mainIframe;
              }

              function postChatbotScreen(tab) {
                var mainIframe = getMainIframe();
                if (!mainIframe || !mainIframe.contentWindow) return;

                if (tab === 'welcome') {
                  mainIframe.contentWindow.postMessage({ type: 'showWelcomeScreen' }, '*');
                } else if (tab === 'conversation') {
                  mainIframe.contentWindow.postMessage({ type: 'showConversationScreen' }, '*');
                }
              }

              function scheduleChatbotScreen(tab) {
                if (!tab || tab === 'advanced') return;

                function applyChatbotScreen(nextTab) {
                  if (typeof window.ChatbotBuilder !== 'undefined' && typeof window.ChatbotBuilder.showScreen === 'function') {
                    window.ChatbotBuilder.showScreen(nextTab);
                  }
                  postChatbotScreen(nextTab);
                }

                applyChatbotScreen(tab);

                [100, 500, 1000, 2000].forEach(function(delay) {
                  setTimeout(function() {
                    applyChatbotScreen(window.pendingChatbotTab || tab);
                  }, delay);
                });
              }

              window.addEventListener('message', function(event) {
                if (event.data && event.data.type === 'LIVEBOT_STATIC_MESSAGES_READY') {
                  window.parent.postMessage(event.data, '*');
                  return;
                }

                if (event.data && event.data.type === 'SEND_INITIAL_MESSAGE') {
                  var mainIframe = getMainIframe();
                  if (mainIframe && mainIframe.contentWindow && event.data.messages) {
                    mainIframe.contentWindow.postMessage({
                      type: 'sendStaticMessages',
                      payload: { messages: event.data.messages }
                    }, '*');
                    console.log('✅ Sent static initial messages to inner iframe');
                  } else {
                    console.warn('⚠️ Inner iframe not available for static messages');
                  }
                }
              });
`;

      const iframeContent = `
          <!DOCTYPE html>
          <html>
          <head>
            <meta charset="UTF-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <title>Chatbot Preview</title>
            <style>
              body {
                margin: 0;
                padding: 0;
                background: transparent !important;
              }
            </style>
          </head>
          <body>
            <script>
              window.cbbSettings = {
                base_url: "${liveUrl}",
                app_id: "${appId}",
                app_type: "chat"
              };
              ${iframeScreenSyncScript}
  
              // Listen for messages from parent and relay to inner iframe
              window.addEventListener('message', function(event) {
  
                if(event.data && event.data.type === 'UPDATE_EMBED_CONFIG') {
                  if (typeof window.updateEmbedConfig === 'function') {
                    window.updateEmbedConfig(event.data.embedConfig);
                    console.log('✅ Called updateEmbedConfig');
                  } else {
                    console.warn('⚠️ updateEmbedConfig not available, falling back to reload');
                    window.reloadPreviewIframe();
                  }
                  return;
                }
  
                if(event.data && event.data.type === 'CHANGE_CHATBOT_TAB') {
                  window.pendingChatbotTab = event.data.chatBotTab || "${INITIAL_IFRAME_TAB}";
                  if (event.data.chatBotTab === 'advanced') {
                    if (typeof window.ChatbotBuilder !== 'undefined' && typeof window.ChatbotBuilder.close === 'function') {
                      window.ChatbotBuilder.close();
                      console.log('✅ Closed chatbot for advanced tab');
                    } else {
                      var mainIframe = getMainIframe();
                      if (mainIframe) {
                        mainIframe.style.display = 'none';
                      }
                      console.log('⚠️ ChatbotBuilder.close unavailable, hid iframe for advanced tab');
                    }
                  } else if (typeof window.ChatbotBuilder !== 'undefined') {
                    if (typeof window.ChatbotBuilder.open === 'function') {
                      window.ChatbotBuilder.open();
                    }
                    if (typeof window.ChatbotBuilder.showScreen === 'function') {
                      scheduleChatbotScreen(event.data.chatBotTab);
                    }
                    var mainIframe = getMainIframe();
                    if (mainIframe) {
                      mainIframe.style.display = '';
                    }
                    console.log('✅ Changed to tab:', event.data.chatBotTab);
                  }
                  return;
                }
  
                if(event.data && event.data.type === 'CREDIT_CONSUMED') {
                  window.parent.postMessage(event.data, '*');
                  return;
                }

                if (event.data && event.data.type === 'LIVEBOT_PREVIEW_TAB_CHANGE') {
                  window.parent.postMessage(event.data, '*');
                  return;
                }
  
                if (event.data && event.data.type === 'BOT_DATA_UPDATE') {
                  // Find the inner iframe and forward the message
                  var mainIframe = getMainIframe();
                  if (mainIframe && mainIframe.contentWindow) {
                    mainIframe.contentWindow.postMessage(event.data, '*');
                  }
                  // Also store the data for when the iframe loads
                  window.latestBotData = event.data;
                }
              });
              
  
              // Observe DOM for when main-iframe is created by embed.js
              var observer = new MutationObserver(function(mutations) {
                var mainIframe = getMainIframe();
                if (mainIframe) {
                  mainIframe.addEventListener('load', function() {
                    // Send latest data when iframe loads
                    if (window.latestBotData && mainIframe.contentWindow) {
                      mainIframe.contentWindow.postMessage(window.latestBotData, '*');
                    }
                    scheduleChatbotScreen(window.pendingChatbotTab);
                  });
                  observer.disconnect();
                }
              });
              observer.observe(document.body, { childList: true, subtree: true });
  
              
              //window.ChatbotBuilder.showScreen('${INITIAL_IFRAME_TAB}');
              function openChatbotWhenready() {
                if(typeof window.ChatbotBuilder !== 'undefined') {
                  var chatbotTab = window.pendingChatbotTab || "${INITIAL_IFRAME_TAB}";
                  if (chatbotTab === 'advanced') {
                    if (typeof window.ChatbotBuilder.close === 'function') {
                      window.ChatbotBuilder.close();
                    } else {
                      var mainIframe = getMainIframe();
                      if (mainIframe) {
                        mainIframe.style.display = 'none';
                      }
                    }
                  } else {
                    if (typeof window.ChatbotBuilder.open === 'function') {
                      window.ChatbotBuilder.open();
                    }
                    if (typeof window.ChatbotBuilder.showScreen === 'function') {
                      scheduleChatbotScreen(chatbotTab);
                    }
                    var mainIframe = getMainIframe();
                    if (mainIframe) {
                      mainIframe.style.display = '';
                    }
                    console.log('✅ Called ChatbotBuilder.open() on load');
                  }
                } else {
                  console.log('Waiting for ChatbotBuilder');
                  setTimeout(openChatbotWhenready, 500);  
                }
              }
  
            </script>
            <script 
              src="${liveUrl}/embed.js" 
              defer
              onload="var loading = document.querySelector('.loading'); if(loading) loading.style.display='none'; openChatbotWhenready();"
              onerror="var loading  = document.querySelector('.loading'); if(loading) loading.innerText='Failed to load agent';"
            ></script>
          </body>
          </html>
        `;

      const blob = new Blob([iframeContent], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      iframeUrlRef.current = url;
      setIframeUrl(url);
    }
  }, [shouldLoadLivePreview, liveUrl, botPreviewUrl]);

  useEffect(() => {
    if (!shouldLoadLivePreview || !iframeUrl) return;

    const iframe =
      appearancePreviewTab === "welcome"
        ? welcomeIframeRef.current
        : conversationIframeRef.current;
    if (iframe?.contentWindow) {
      iframe.contentWindow.postMessage(
        {
          type: "CHANGE_CHATBOT_TAB",
          chatBotTab: appearancePreviewTab,
        },
        "*",
      );
    }
  }, [appearancePreviewTab, iframeUrl, shouldLoadLivePreview]);

  useEffect(() => {
    const handleLivebotPreviewTabChange = (event: MessageEvent) => {
      if (event.data?.type !== "LIVEBOT_PREVIEW_TAB_CHANGE") return;

      const nextTab = event.data.tab;
      if (nextTab !== "welcome" && nextTab !== "conversation") return;

      setAppearancePreviewTab(nextTab);
    };

    window.addEventListener("message", handleLivebotPreviewTabChange);

    return () => {
      window.removeEventListener("message", handleLivebotPreviewTabChange);
    };
  }, [setAppearancePreviewTab]);

  useEffect(() => {
    const handleStaticMessagesReady = (event: MessageEvent) => {
      if (event.data?.type !== "LIVEBOT_STATIC_MESSAGES_READY") return;
      if (event.source !== conversationIframeRef.current?.contentWindow) return;
      setStaticMessagesReady(true);
    };

    window.addEventListener("message", handleStaticMessagesReady);
    return () => {
      window.removeEventListener("message", handleStaticMessagesReady);
    };
  }, []);

  useEffect(() => {
    if (!staticMessagesReady || hasSentInitialMessageRef.current) return;

    const hasTrainedData = Boolean(
      botData?.aiSource?.urls?.length ||
      botData?.aiSource?.files?.length ||
      botData?.aiSource?.texts?.length ||
      botData?.aiSource?.qnas?.length,
    );
    if (!hasTrainedData) return;

    hasSentInitialMessageRef.current = true;
    conversationIframeRef.current?.contentWindow?.postMessage(
      {
        type: "SEND_INITIAL_MESSAGE",
        messages: STATIC_PREVIEW_MESSAGES,
      },
      "*",
    );
  }, [botData, staticMessagesReady]);

  useEffect(() => {
    if (!shouldLoadLivePreview || !iframeUrl || !botData?.chatConfig) return;

    const timeoutId = window.setTimeout(() => {
      [welcomeIframeRef.current, conversationIframeRef.current].forEach(
        (iframe) => {
          if (!iframe?.contentWindow) return;

          iframe.contentWindow.postMessage(
            {
              type: "BOT_DATA_UPDATE",
              botData,
              chatConfig: botData.chatConfig,
              timestamp: Date.now(),
            },
            "*",
          );
        },
      );
    }, 500);

    return () => window.clearTimeout(timeoutId);
  }, [iframeUrl, botData, shouldLoadLivePreview]);

  const handleIframeLoad = (tab: AppearancePreviewTab) => {
    if (tab === "welcome") {
      setWelcomeIframeLoaded(true);
    } else {
      setConversationIframeLoaded(true);
    }

    window.setTimeout(() => {
      const iframe =
        tab === "welcome"
          ? welcomeIframeRef.current
          : conversationIframeRef.current;
      if (!iframe?.contentWindow || !botData?.chatConfig) return;

      iframe.contentWindow.postMessage(
        {
          type: "BOT_DATA_UPDATE",
          botData,
          chatConfig: botData.chatConfig,
          timestamp: Date.now(),
        },
        "*",
      );

      iframe.contentWindow.postMessage(
        {
          type: "CHANGE_CHATBOT_TAB",
          chatBotTab: tab,
        },
        "*",
      );
    }, 1000);
  };

  const shouldMountIframe = Boolean(iframeUrl);
  const activeIframeLoaded =
    appearancePreviewTab === "welcome"
      ? welcomeIframeLoaded
      : conversationIframeLoaded;
  const shouldDisplayLivePreview = shouldLoadLivePreview && shouldMountIframe;
  const isWelcomeIframeVisible =
    shouldDisplayLivePreview &&
    appearancePreviewTab === "welcome" &&
    welcomeIframeLoaded;
  const isConversationIframeVisible =
    shouldDisplayLivePreview &&
    appearancePreviewTab === "conversation" &&
    conversationIframeLoaded;
  const shouldShowPlaceholder = !shouldLoadLivePreview || !activeIframeLoaded;

  return (
    <motion.div
      className={`onboarding-preview-wrapper flex w-full flex-col items-center justify-center rounded-[40px] px-[16px] py-[20px] max-[1200px]:min-h-[540px] max-[1200px]:rounded-[28px] lg:w-[50%] ${className}`}
      initial={{ opacity: 0, scale: 0.985, x: 16 }}
      animate={{ opacity: 1, scale: 1, x: 0 }}
      transition={{ duration: 0.42, ease: [0.42, 0, 0.58, 1], delay: 0.06 }}
    >
      <motion.div
        className="relative flex h-full w-full flex-col items-center justify-center gap-[25px] py-[20px] max-[1200px]:gap-[15px]"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 12 }}
        transition={{ duration: 0.22, ease: "easeOut" }}
      >
        {shouldDisplayLivePreview && (
          <div className="z-[8] grid h-[39px] w-full max-w-[254px] shrink-0 grid-cols-2 rounded-[12px] bg-white p-[4px] shadow-[0_12px_34px_rgba(79,50,120,0.12)]">
            {appearanceTabs.map((tab) => {
              const isActive = appearancePreviewTab === tab.id;

              return (
                <button
                  key={tab.id}
                  className={`relative rounded-[10px] text-[12px] font-medium leading-[110%] transition-colors ${
                    isActive ? "font-medium text-[#FB5F66]" : "text-[#666]"
                  }`}
                  type="button"
                  onClick={() => setAppearancePreviewTab(tab.id)}
                >
                  {isActive && (
                    <motion.span
                      className="absolute inset-0 rounded-[10px] bg-white shadow-[0_1px_9px_0_rgba(0,0,0,0.15)]"
                      layoutId="appearance-preview-tab-bg"
                      transition={{
                        type: "spring",
                        stiffness: 420,
                        damping: 34,
                      }}
                    />
                  )}
                  <span
                    className={`relative z-10 text-[12px] ${isActive ? "font-medium" : ""}`}
                  >
                    {tab.label}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        <div className="agent_preview_wrapper" aria-hidden="true">
          {shouldMountIframe && (
            <>
              <iframe
                ref={welcomeIframeRef}
                src={`${iframeUrl}#welcome`}
                allow="microphone *; clipboard-write *"
                title="Chatbot Welcome Preview"
                className="ai-bot-preview fx fx-d-col bot-prev-responsive h-full w-full"
                sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                onLoad={() => handleIframeLoad("welcome")}
                aria-hidden={!isWelcomeIframeVisible}
                style={{
                  background: "transparent",
                  border: "none",
                  display: isWelcomeIframeVisible ? "block" : "none",
                  inset: 0,
                  opacity: isWelcomeIframeVisible ? 1 : 0,
                  pointerEvents: isWelcomeIframeVisible ? "auto" : "none",
                  position: "absolute",
                  boxShadow: "0 0 60px 0 rgba(0, 0, 0, 0.10)",
                  borderRadius: "24px",
                  visibility: isWelcomeIframeVisible ? "visible" : "hidden",
                  zIndex: isWelcomeIframeVisible ? 2 : 1,
                }}
              />
              <iframe
                ref={conversationIframeRef}
                src={`${iframeUrl}#conversation`}
                allow="microphone *; clipboard-write *"
                title="Chatbot Conversation Preview"
                className="ai-bot-preview fx fx-d-col bot-prev-responsive h-full w-full"
                sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                onLoad={() => handleIframeLoad("conversation")}
                aria-hidden={!isConversationIframeVisible}
                style={{
                  background: "transparent",
                  border: "none",
                  display: isConversationIframeVisible ? "block" : "none",
                  inset: 0,
                  opacity: isConversationIframeVisible ? 1 : 0,
                  pointerEvents: isConversationIframeVisible ? "auto" : "none",
                  position: "absolute",
                  borderRadius: "24px",
                  boxShadow: "0 0 60px 0 rgba(0, 0, 0, 0.10)",
                  visibility: isConversationIframeVisible
                    ? "visible"
                    : "hidden",
                  zIndex: isConversationIframeVisible ? 2 : 1,
                }}
              />
            </>
          )}
          {shouldShowPlaceholder && (
            <AgentPreviewPlaceholder loading={shouldLoadLivePreview} />
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default OnBoardingPreview;
